import {
  NexusAddon,
  NexusMultiSelectSelection,
  NexusPricingEntry,
  NexusProduct,
  NexusPromotion,
  NexusVariant,
} from "@/types/nexus-product";
import {
  computeDiscountedPriceGrosze,
  formatPromotionBadgeLabel,
  resolvePromotion,
} from "@/utils/promotions";
import { resolveAddonPriceForVariantIds } from "./nexusVariants";

export const HALF_AND_HALF_LABEL = "Pół na pół";

export type HalfConfig = {
  product: NexusProduct;
  entry: NexusPricingEntry;
  multiSelectSelections: NexusMultiSelectSelection[];
  addons: NexusAddon[];
};

export type HalfAddonLine = {
  id: string;
  name: string;
  price: number;
};

export type PricedHalf = {
  rawBasePrice: number;
  basePrice: number;
  promotion: NexusPromotion | null;
  addons: HalfAddonLine[];
};

export type HalfAndHalfPricing = {
  halves: [PricedHalf, PricedHalf];
  rawBasePrice: number;
  basePrice: number;
  addonsTotal: number;
  unitPrice: number;
  rawUnitPrice: number;
  promotion: NexusPromotion | null;
};

export type CartHalf = {
  productId: string;
  productName: string;
  pricingKey: string;
  multiSelectSelections: NexusMultiSelectSelection[];
  addons: HalfAddonLine[];
};

export function normalizeVariantName(name: string | null | undefined): string {
  return (name ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

export function canBeHalf(product: NexusProduct): boolean {
  return (
    product.halfAndHalf === true &&
    !product.disabled &&
    product.saleUnit !== "weight"
  );
}

export function findSizeVariant(
  product: NexusProduct,
  entry: NexusPricingEntry | undefined
): NexusVariant | null {
  const group = product.variantGroups[0];
  if (!group || !entry) return null;
  return group.variants.find((v) => entry.variantIds.includes(v.id)) ?? null;
}

export function findMatchingSizeVariant(
  product: NexusProduct,
  sizeName: string
): NexusVariant | null {
  const group = product.variantGroups[0];
  if (!group) return null;
  const target = normalizeVariantName(sizeName);
  return (
    group.variants.find(
      (v) => !v.disabled && normalizeVariantName(v.name) === target
    ) ?? null
  );
}

export function listHalfPartners(
  products: NexusProduct[],
  first: NexusProduct,
  firstSizeName: string | null
): NexusProduct[] {
  if (!canBeHalf(first)) return [];
  const firstHasGroups = first.variantGroups.length > 0;

  return products.filter((candidate) => {
    if (!canBeHalf(candidate)) return false;
    if (candidate.hidden && candidate.id !== first.id) return false;
    const candidateHasGroups = candidate.variantGroups.length > 0;
    if (!firstHasGroups || !candidateHasGroups) {
      return !firstHasGroups && !candidateHasGroups;
    }
    if (firstSizeName === null) return false;
    const variant = findMatchingSizeVariant(candidate, firstSizeName);
    if (!variant) return false;
    return Object.values(candidate.pricing).some(
      (entry) => !entry.disabled && entry.variantIds.includes(variant.id)
    );
  });
}

export function halfVariantIds(
  entry: NexusPricingEntry | undefined,
  pricingSelections: string[],
  multiSelectSelections: NexusMultiSelectSelection[]
): string[] {
  return [
    ...(entry ? entry.variantIds : pricingSelections),
    ...multiSelectSelections.flatMap((selection) => selection.variantIds),
  ];
}

export function halveAddonPrice(fullPrice: number): number {
  return Math.round(fullPrice / 2);
}

function priceHalf(half: HalfConfig, promotions: NexusPromotion[]): PricedHalf {
  const promotion = resolvePromotion(
    promotions,
    half.product.id,
    half.entry.variantIds[0] ?? null,
    half.product.categories
  );
  const rawBasePrice = half.entry.price;
  const basePrice = promotion
    ? computeDiscountedPriceGrosze(rawBasePrice, promotion)
    : rawBasePrice;
  const variantIds = halfVariantIds(half.entry, [], half.multiSelectSelections);

  return {
    rawBasePrice,
    basePrice,
    promotion: basePrice !== rawBasePrice ? promotion : null,
    addons: half.addons.map((addon) => ({
      id: addon.id,
      name: addon.name,
      price: halveAddonPrice(resolveAddonPriceForVariantIds(addon, variantIds)),
    })),
  };
}

export function priceHalfAndHalf(
  halves: [HalfConfig, HalfConfig],
  promotions: NexusPromotion[]
): HalfAndHalfPricing {
  const first = priceHalf(halves[0], promotions);
  const second = priceHalf(halves[1], promotions);
  const basePrice = Math.round((first.basePrice + second.basePrice) / 2);
  const rawBasePrice = Math.round(
    (first.rawBasePrice + second.rawBasePrice) / 2
  );
  const addonsTotal = [...first.addons, ...second.addons].reduce(
    (sum, addon) => sum + addon.price,
    0
  );

  return {
    halves: [first, second],
    rawBasePrice,
    basePrice,
    addonsTotal,
    unitPrice: basePrice + addonsTotal,
    rawUnitPrice: rawBasePrice + addonsTotal,
    promotion:
      basePrice !== rawBasePrice ? (first.promotion ?? second.promotion) : null,
  };
}

function halfChoiceLabels(half: HalfConfig): string[] {
  const groups = half.product.variantGroups;
  const sizeGroupId = groups[0]?.id;

  const singleSelect = groups
    .filter(
      (group) =>
        !group.multiSelect && !group.displayWhen && group.id !== sizeGroupId
    )
    .map((group) => {
      const variant = group.variants.find((v) =>
        half.entry.variantIds.includes(v.id)
      );
      return variant ? `${group.name}: ${variant.name}` : null;
    })
    .filter((label): label is string => Boolean(label));

  const multiSelect = groups
    .filter((group) => group.multiSelect)
    .map((group) => {
      const selection = half.multiSelectSelections.find(
        (s) => s.groupId === group.id
      );
      const names = (selection?.variantIds ?? [])
        .map((id) => group.variants.find((v) => v.id === id)?.name)
        .filter((name): name is string => Boolean(name));
      return names.length ? `${group.name}: ${names.join(", ")}` : null;
    })
    .filter((label): label is string => Boolean(label));

  return [...singleSelect, ...multiSelect];
}

function halfLabel(half: HalfConfig, position: number): string {
  const label = `Połówka ${position}: ${half.product.name}`;
  const choices = halfChoiceLabels(half);
  return choices.length ? `${label} (${choices.join("; ")})` : label;
}

export function buildHalfAndHalfOptions(
  halves: [HalfConfig, HalfConfig]
): string[] {
  const [first, second] = halves;
  const sizeGroup = first.product.variantGroups[0];
  const sizeVariant = findSizeVariant(first.product, first.entry);
  const sizeLabel =
    sizeGroup && sizeVariant && (sizeGroup.name.trim() || sizeVariant.name.trim())
      ? `${sizeGroup.name}: ${sizeVariant.name}`
      : null;

  return [
    HALF_AND_HALF_LABEL,
    ...(sizeLabel ? [sizeLabel] : []),
    halfLabel(first, 1),
    halfLabel(second, 2),
  ];
}

export function buildHalfAndHalfCartId(
  halves: [HalfConfig, HalfConfig]
): string {
  const [first, second] = halves;
  const addonIds = (half: HalfConfig) =>
    half.addons
      .map((addon) => addon.id)
      .sort()
      .join(",");

  return `${first.product.id}:${first.entry.key}-addons-half~${second.product.id}:${second.entry.key}~${addonIds(first)}~${addonIds(second)}`;
}

export function buildHalfAndHalfCartItem(
  halves: [HalfConfig, HalfConfig],
  promotions: NexusPromotion[]
) {
  const pricing = priceHalfAndHalf(halves, promotions);
  const [first, second] = halves;
  const toCartAddon = (addon: HalfAddonLine) => ({
    ...addon,
    price: addon.price / 100,
  });

  return {
    id: buildHalfAndHalfCartId(halves),
    name: `${first.product.name} / ${second.product.name}`,
    price: pricing.unitPrice / 100,
    basePrice: pricing.basePrice / 100,
    ...(pricing.promotion
      ? {
          originalPrice: pricing.rawUnitPrice / 100,
          originalBasePrice: pricing.rawBasePrice / 100,
          discountLabel: formatPromotionBadgeLabel(pricing.promotion),
        }
      : {}),
    currency: "pln",
    image: first.product.image || "",
    options: buildHalfAndHalfOptions(halves),
    multiSelectSelections: [],
    addons: pricing.halves.flatMap((half, index) =>
      half.addons.map((addon) =>
        toCartAddon({
          ...addon,
          name: `${addon.name} (połówka ${index + 1})`,
        })
      )
    ),
    halves: halves.map(
      (half, index): CartHalf => ({
        productId: half.product.id,
        productName: half.product.name,
        pricingKey: half.entry.key,
        multiSelectSelections: half.multiSelectSelections,
        addons: pricing.halves[index].addons.map(toCartAddon),
      })
    ),
  };
}
